import { useCallback } from 'react';
import { useAppSelector } from 'app/hooks';
import {
  selectIsUserAgent,
  selectSessionLastVisitedAt,
  selectTourDontShow,
  selectTourSkipCount,
} from 'services/session/sessionSelectors';
import { TOUR_MAX_SKIPS } from './constants';
import { markTourDismissed, useIsTourDismissed } from './tourDismissedStore';

/**
 * Eligibility gate for the dashboard onboarding tour.
 *
 * Four conditions, all of which must hold:
 *
 *   !dontShowTour                     // server: permanent opt-out
 *   && skipTourCount < TOUR_MAX_SKIPS // server: 3 skips and it stops
 *   && !isAgent                       // never under masquerade
 *   && !alreadyViewedThisSession      // sessionStorage: already seen this session
 *
 * The two spans are deliberate and independent:
 *
 *  - `alreadyViewedThisSession` comes from `tourDismissedStore`, written by
 *    `markDismissed()` when the user actually dismisses the tour — NOT when it
 *    opens. It is backed by `sessionStorage`, so it survives F5, and is cleared
 *    on sign-out (`app/store.ts`). It is read through `useSyncExternalStore`
 *    rather than `sessionStorage` directly because it is also what releases
 *    every other automatic popup (`useIsTourBlocking`): a plain storage read
 *    would not re-render them, which is why the exit path used to need a full
 *    page reload. Because it is not written on open, a user who refreshes
 *    MID-tour correctly sees the tour again — they have not chosen anything
 *    yet.
 *  - The server attributes only take effect at the NEXT sign-in, because nothing
 *    re-issues `POST api/client/sessions` mid-session.
 *
 * Divergences from Angular (`dashboard-main.component.ts:311,319`):
 *
 *  1. No `lastAction !== 'REFRESH_TOKEN' / 'CREATE_AP_SESSION'` guard — React's
 *     session slice has no `lastAction`, and none is invented: Angular needs it
 *     because its gate lives in a store subscription that re-fires on every
 *     session action, whereas this is read once per mount and the dismissal key
 *     makes re-entry idempotent.
 *  2. `state.userLoggedIn` is implicit — the dashboard route is behind auth.
 *  3. `migratedAt === null` dropped by product decision (2026-08-21): migrated
 *     users see the tour like anyone else, and Angular's replacement modal was
 *     never migrated.
 *  4. `lastVisitedAt` is NOT in the gate (2026-08-27). It selects the welcome
 *     copy only — "built" for a first visit, "redesigned" otherwise. Keeping it
 *     as a gate condition would have made this whole feature inert: the backend
 *     stamps it on first login, so a skipper could never return and the
 *     "redesigned" string would be unreachable.
 *
 * `isFirstVisit` uses a falsy check rather than `=== null` because the field
 * initialises to `''` in some paths as well as `null`.
 */
function useDashboardTourGate() {
  const lastVisitedAt = useAppSelector(selectSessionLastVisitedAt);
  const skipTourCount = useAppSelector(selectTourSkipCount);
  const dontShowTour = useAppSelector(selectTourDontShow);
  const isAgent = useAppSelector(selectIsUserAgent);

  /* Only picks the welcome copy — NOT part of the gate. See divergence 4. */
  const isFirstVisit = !lastVisitedAt;

  const alreadyViewedThisSession = useIsTourDismissed();

  const isEligible =
    !dontShowTour &&
    skipTourCount < TOUR_MAX_SKIPS &&
    !isAgent &&
    !alreadyViewedThisSession;

  /**
   * Records that the user DISMISSED the tour in this browser session — called
   * from `close()` on Skip / "Don't show again" / Finish, never on open.
   *
   * Angular wrote this key at open time, and so did this hook originally,
   * because the key was then the only suppression there was: without it an
   * accidental refresh would reopen the tour forever. Now that the server holds
   * `skipTourCount` / `dontShowTour`, the key's job is narrower and open-time
   * writing became actively wrong — a user who refreshed mid-tour lost it
   * despite never having chosen to dismiss it.
   *
   * It is still needed for Skip specifically: skipping writes
   * `skipTourCount: 1`, which still satisfies `< 3`, so without this key a
   * refresh would reopen the very tour the user just dismissed.
   *
   * It is also the handover signal: flipping it makes `isEligible` false, which
   * releases `SessionModalSequence`, the contact-details popup and the
   * child-aged-out popup (`useIsTourBlocking`). Written independently of the
   * attribute POST, so the dismissal is honoured for this session even if that
   * request fails or the tab is closed mid-flight.
   */
  const markDismissed = useCallback(() => {
    markTourDismissed();
  }, []);

  return { isEligible, markDismissed, skipTourCount, isFirstVisit };
}

export default useDashboardTourGate;
